// Importing CSS this way gets it minified by the build tool
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "src/css/App.css";
import * as React from "react";
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Header from "src/components/Header";
import PrivateRoute from "src/components/PrivateRoute";
import SiteInfo from "src/components/SiteInfo";
import { LiveChatWidget } from "@livechat/widget-react";
import { UploadListProvider } from "src/contexts/UploadListContext";
import { useAppSelector } from "src/app/hooks";
import { selectAuth } from "src/features/authSlice";

type SiteLayoutProps = {
  children: React.ReactNode;
  isLeftSidebarOpen?: boolean;
};

const SITE_INFO_BAR_HEIGHT = 38; // Height of the account info bar at the bottom

const SiteLayout: React.FC<SiteLayoutProps> = ({ children }) => {
  const liveChatLicense = "9040610";
  const { isEmailVerified, userEmail, isNXXG, acctMessage } =
    useAppSelector(selectAuth);
  const [isEmailBannerDismissed, setIsEmailBannerDismissed] = useState(false);

  // When opened from the reseller portal popup (/trustcall/reseller/*), hide
  // the top nav, email banner, site info bar, and live chat — matching MVC
  // Monitor. The reseller entry-point route sets a sessionStorage flag that
  // persists for the lifetime of the popup tab.
  const location = useLocation();
  const hideNav = location.pathname.startsWith("/trustcall")
    && (sessionStorage.getItem("hideNav") === "reseller"
      || location.pathname.startsWith("/trustcall/reseller"));

  const handleDismissEmailBanner = () => {
    setIsEmailBannerDismissed(true);
  };

  const shouldShowEmailBanner =
    (!isEmailVerified || !userEmail) && !isEmailBannerDismissed && !isNXXG;

  // When display of the email banner or site info bar is toggled, change the
  // variable that's used to set the height of the divs below the nav bar.
  // If not done the bottom part of the middle section isn't visible.
  useEffect(() => {
    if (hideNav) {
      document.documentElement.style.setProperty("--height-below-navbar", "100vh");
      return;
    }
    const bannerHeight = shouldShowEmailBanner ? 40 : 0;
    const siteInfoHeight = acctMessage ? SITE_INFO_BAR_HEIGHT : 0;
    document.documentElement.style.setProperty(
      "--height-below-navbar",
      `calc(100vh - var(--height-of-top-nav-bar) - ${bannerHeight + siteInfoHeight}px)`,
    );
  }, [shouldShowEmailBanner, acctMessage, hideNav]);

  return (
    <PrivateRoute>
      <Helmet>
        <title>DNCScrub</title>
        <link
          href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.10.5/font/bootstrap-icons.css"
          rel="stylesheet"
        />
      </Helmet>
      <UploadListProvider>
        {!hideNav && <Header />}

        {/* Email verification banner */}
        {!hideNav && shouldShowEmailBanner && (
          <div
            className="alert alert-warning d-flex align-items-center justify-content-between mb-0"
            style={{
              borderRadius: 0,
              padding: "6px 12px",
              minHeight: "auto",
              fontSize: "14px",
            }}
          >
            <div className="d-flex align-items-center">
              <i
                className="bi bi-exclamation-triangle-fill me-2"
                style={{ fontSize: "16px" }}
              ></i>
              <span>
                {!userEmail
                  ? "You do not have any email setup. You need to enter one and verify your email address before you have full access to Help desk. "
                  : "Your email address is not verified. To get access to full functionality of the helpdesk this is required. "}
                <Link to="/verify-email" className="alert-link">
                  Please click here to verify your email address
                </Link>
                .
              </span>
            </div>
            <button
              type="button"
              className="btn-close on-a-yellow-background"
              aria-label="Close"
              onClick={handleDismissEmailBanner}
              style={{ fontSize: "12px", width: "16px", height: "16px" }}
            ></button>
          </div>
        )}

        {/* content area begin */}
        <div className={hideNav ? "" : "container-below-navbar"}>{children}</div>
        {/* content area end */}
      </UploadListProvider>

      {!hideNav && <SiteInfo />}
      {!hideNav && <LiveChatWidget license={liveChatLicense} />}
    </PrivateRoute>
  );
};

export default SiteLayout;
